import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { submitContactRequest } from '../../services/contact';
import { useContactInformation } from '../../hooks/useContactInformation';
import { mapProfileToForm } from '../../utils/profileMappers';
import ProfileField from './ProfileField';

const SOCIAL_CONFIG = [
  {
    key: 'facebook_url',
    label: 'Facebook',
    icon: (
      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path d="M22 12a10 10 0 10-11.6 9.87v-6.99H8.1V12h2.3V9.8c0-2.27 1.35-3.53 3.43-3.53.99 0 2.03.18 2.03.18v2.24h-1.14c-1.13 0-1.48.7-1.48 1.42V12h2.52l-.4 2.88h-2.12v6.99A10 10 0 0022 12z" />
      </svg>
    ),
  },
  {
    key: 'instagram_url',
    label: 'Instagram',
    icon: (
      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path d="M7 2h10a5 5 0 015 5v10a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5zm5 5a5 5 0 100 10 5 5 0 000-10zm6.5-.75a1.25 1.25 0 11-2.5 0 1.25 1.25 0 012.5 0z" />
      </svg>
    ),
  },
  {
    key: 'youtube_url',
    label: 'YouTube',
    icon: (
      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path d="M21.8 8.001a2.5 2.5 0 00-1.76-1.77C18.36 6 12 6 12 6s-6.36 0-8.04.231A2.5 2.5 0 002.2 8.001 26.3 26.3 0 002 12a26.3 26.3 0 00.2 3.999 2.5 2.5 0 001.76 1.77C5.64 18 12 18 12 18s6.36 0 8.04-.231a2.5 2.5 0 001.76-1.77A26.3 26.3 0 0022 12a26.3 26.3 0 00-.2-3.999zM10 15.5v-7l6 3.5-6 3.5z" />
      </svg>
    ),
  },
  {
    key: 'twitter_url',
    label: 'X',
    icon: (
      <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
];

function ContactInfoItem({ icon, children, href }) {
  const content = href ? (
    <a href={href} className="text-[12px] leading-relaxed text-[#CCCCCC]">
      {children}
    </a>
  ) : (
    <span className="text-[12px] leading-relaxed text-[#CCCCCC]">{children}</span>
  );

  return (
    <li className="flex flex-col items-center gap-2 text-center">
      <span className="shrink-0 text-white">{icon}</span>
      {content}
    </li>
  );
}

export default function ProfileHelpPage() {
  const { user } = useOutletContext() || {};
  const profile = user ? mapProfileToForm(user) : null;
  const { contact, loading: contactLoading } = useContactInformation();

  const [form, setForm] = useState({
    fullName: '',
    mobile: '',
    email: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const socialLinks = useMemo(
    () =>
      SOCIAL_CONFIG.flatMap((social) => {
        const href = contact?.[social.key];
        return href ? [{ ...social, href }] : [];
      }),
    [contact]
  );

  useEffect(() => {
    if (!profile) return;
    setForm((prev) => ({
      ...prev,
      fullName: profile.fullName || prev.fullName,
      mobile: profile.mobile || prev.mobile,
      email: profile.email || prev.email,
    }));
  }, [profile?.fullName, profile?.mobile, profile?.email]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      const res = await submitContactRequest({
        fullName: form.fullName.trim(),
        mobile: form.mobile.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });

      setToast({
        message: res?.message || 'Your message has been sent successfully',
        type: 'success',
      });
      setForm((prev) => ({ ...prev, message: '' }));
    } catch (e) {
      setToast({
        message: e?.message || 'Failed to send your message. Please try again.',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {toast && (
        <div
          role="status"
          className={`fixed left-1/2 top-6 z-[60] -translate-x-1/2 rounded-lg px-4 py-2.5 text-sm font-medium text-white shadow-lg ${
            toast.type === 'error' ? 'bg-red-600' : 'bg-[#2e7d32]'
          }`}
        >
          {toast.message}
        </div>
      )}

      <main className="mx-auto w-full max-w-[1040px] rounded-2xl p-6 sm:px-8 sm:py-10">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#1B1B1B] sm:text-[32px]">Contact Us</h1>
          <p className="mx-auto mt-3 max-w-2xl text-[15px] text-[#666666]">
            Any question or remarks? Just write us a message!
          </p>
        </div>

        <div className="relative mx-auto mt-10 w-full max-w-[1000px] lg:px-10">
          <div className="relative overflow-visible lg:pl-24">
            <aside className="relative z-20 overflow-hidden rounded-[20px] bg-gradient-to-b from-[#9369CC] to-[#201333] px-8 text-white shadow-[0_24px_60px_rgba(40,20,80,0.45)] max-lg:rounded-b-none lg:absolute lg:-left-14 lg:top-1/2 lg:w-[350px] lg:-translate-y-1/2 lg:px-10 lg:py-10">
              <div className="z-10 flex min-h-[400px] flex-col lg:min-h-[440px]">
                <div className="text-center">
                  <h2 className="text-[20px] font-semibold leading-tight text-[#FFFFFF]">
                    {contact?.title || 'Contact Information'}
                  </h2>
                  <p className="mt-3 text-[14px] font-medium text-[#C9C9C9]">
                    {contact?.subtitle || 'Say something to start a live chat!'}
                  </p>

                  <ul className="mt-4 space-y-3 lg:mt-6 lg:space-y-6">
                    {contactLoading ? (
                      <>
                        <li className="mx-auto h-10 w-40 animate-pulse rounded bg-white/20" />
                        <li className="mx-auto h-10 w-48 animate-pulse rounded bg-white/20" />
                        <li className="mx-auto h-10 w-36 animate-pulse rounded bg-white/20" />
                      </>
                    ) : (
                      <>
                        {contact?.address ? (
                          <ContactInfoItem
                            icon={
                              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            }
                          >
                            {contact.address}
                          </ContactInfoItem>
                        ) : null}

                        {contact?.email ? (
                          <ContactInfoItem
                            href={`mailto:${contact.email}`}
                            icon={
                              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                            }
                          >
                            {contact.email}
                          </ContactInfoItem>
                        ) : null}

                        {contact?.phone ? (
                          <ContactInfoItem
                            href={`tel:${contact.phone.replace(/\s/g, '')}`}
                            icon={
                              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                            }
                          >
                            {contact.phone}
                          </ContactInfoItem>
                        ) : null}
                      </>
                    )}
                  </ul>
                </div>

                {socialLinks.length > 0 ? (
                  <div className="mt-6 mt-auto flex items-center gap-4 self-center">
                    {socialLinks.map((social) => (
                      <a
                        key={social.label}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={social.label}
                        className="flex h-[24px] w-[24px] items-center justify-center rounded-full bg-white text-[#281483] transition"
                      >
                        {social.icon}
                      </a>
                    ))}
                  </div>
                ) : null}
              </div>
            </aside>

            <div className="relative z-10 rounded-xl bg-white shadow-xl max-lg:-mt-1 max-lg:rounded-t-none lg:min-h-[520px] lg:min-w-[704px]">
              <form
                onSubmit={handleSubmit}
                className="flex min-h-[480px] flex-col justify-between p-6 pt-10 sm:p-10 lg:min-h-[520px] lg:py-20 lg:pl-[250px] lg:pr-10"
              >
                <div className="space-y-7">
                  <ProfileField
                    variant="underline"
                    icon="user"
                    placeholder="Your full Name"
                    value={form.fullName}
                    onChange={(e) => updateField('fullName', e.target.value)}
                    required
                  />

                  <div className="grid grid-cols-1 gap-7 md:grid-cols-2">
                    <ProfileField
                      variant="underline"
                      icon="phone"
                      placeholder="Mobile No"
                      value={form.mobile}
                      onChange={(e) => updateField('mobile', e.target.value)}
                    />
                    <ProfileField
                      variant="underline"
                      icon="mail"
                      type="email"
                      placeholder="Email Address"
                      value={form.email}
                      onChange={(e) => updateField('email', e.target.value)}
                    />
                  </div>

                  <div className="pt-2">
                    <textarea
                      value={form.message}
                      onChange={(e) => updateField('message', e.target.value)}
                      placeholder="Your message..."
                      rows={8}
                      required
                      className="w-full resize-none rounded-lg border border-[#D9D9D9] bg-white px-4 py-4 text-sm text-[#1B1B1B] outline-none transition placeholder:text-[#999999] focus:border-[#9369CC] focus:ring-1 focus:ring-[#9369CC]"
                    />
                  </div>
                </div>

                <div className="mt-10 flex justify-end">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="min-w-[160px] rounded-full bg-gradient-to-t from-[#6A74E1] to-[#B539FD] px-14 py-2.5 text-base font-medium text-white shadow-[0_10px_24px_rgba(26,135,204,0.4)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
